const SETTLEMENT_OFFER_TIMEZONE = 'America/New_York';
const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const easternDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: SETTLEMENT_OFFER_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const getCalendarDateParts = (expiryDate) => {
  const normalized = String(expiryDate ?? '').trim();
  if (!normalized) return null;

  const isoMatch = normalized.match(ISO_CALENDAR_DATE);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const parts = {
      year: Number(year),
      month: Number(month),
      day: Number(day),
    };
    const validationDate = new Date(
      Date.UTC(parts.year, parts.month - 1, parts.day)
    );
    if (
      validationDate.getUTCFullYear() === parts.year &&
      validationDate.getUTCMonth() === parts.month - 1 &&
      validationDate.getUTCDate() === parts.day
    ) {
      return parts;
    }
    return null;
  }

  const date = new Date(expiryDate);
  if (Number.isNaN(date.getTime())) return null;
  const formattedParts = Object.fromEntries(
    easternDateTimeFormatter
      .formatToParts(date)
      .filter(({ type }) => ['year', 'month', 'day'].includes(type))
      .map(({ type, value }) => [type, Number(value)])
  );
  return formattedParts.year && formattedParts.month && formattedParts.day
    ? formattedParts
    : null;
};

const getEasternOffsetMs = (timestampMs) => {
  const parts = Object.fromEntries(
    easternDateTimeFormatter
      .formatToParts(new Date(timestampMs))
      .filter(({ type }) =>
        ['year', 'month', 'day', 'hour', 'minute', 'second'].includes(type)
      )
      .map(({ type, value }) => [type, Number(value)])
  );
  const representedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return representedAsUtc - Math.floor(timestampMs / 1000) * 1000;
};

/**
 * End of the offer's expiry calendar day in Eastern time (23:59:59.999),
 * matching SettlementOfferLandingPage CountdownTimerComponent semantics.
 * @returns {number|null} epoch ms, or null when expiryDate is missing/invalid
 */
export const getSettlementOfferExpiryEndMs = (expiryDate) => {
  const calendarDate = getCalendarDateParts(expiryDate);
  if (!calendarDate) return null;

  const easternNoonAsUtc = Date.UTC(
    calendarDate.year,
    calendarDate.month - 1,
    calendarDate.day,
    12
  );
  const easternOffsetMs = getEasternOffsetMs(easternNoonAsUtc);
  return (
    Date.UTC(
      calendarDate.year,
      calendarDate.month - 1,
      calendarDate.day,
      23,
      59,
      59,
      999
    ) - easternOffsetMs
  );
};

/** @returns {boolean} true when the offer's EST end-of-day has passed */
export const isSettlementOfferExpired = (expiryDate) => {
  const expiryEndMs = getSettlementOfferExpiryEndMs(expiryDate);
  if (expiryEndMs == null) return false;
  return Date.now() > expiryEndMs;
};

/** Milliseconds until offer expiry end; 0 when expired or date invalid/missing */
export const getSettlementOfferTimeRemainingMs = (expiryDate) => {
  const expiryEndMs = getSettlementOfferExpiryEndMs(expiryDate);
  if (expiryEndMs == null) return 0;
  return Math.max(expiryEndMs - Date.now(), 0);
};
